<template>
  <div :class="`import-content ${!importInPage ? 'twoCols' : ''}`" v-loading="loading">
    <div v-if="!importInPage" class="import-content-left"><i  style="color: #fff;">*</i> 导入进度</div>

    <div class="import-content-right">
      <div class="border-box">
        <!-- 导入状态 -->
        <div class="import-status-msg flex justify-between">
          <span :class="`text-span ${(status === 3 || status === 4) ? 'import-warning': ''}`">{{ statusText[status] }}</span>
          <span class="text-span icon-right" v-if="status === 0 || status === 1" type="text">{{ percentage }}%<i class="QS-icon-loading"/></span>
          <template v-if="status === 2 || (status === 3 && showErrDetail)">
            <a v-if="resultDownload" :href="resultDownload">下载</a>
            <el-button v-else type="text" @click="fetchResultUrl()">下载</el-button>
          </template>
        </div>

        <!-- 导入中 -->
        <el-progress v-if="status === 1" :show-text="false" :stroke-width="6" :percentage="percentage"/>
        <!-- 任务审批等待中的文案提示 -->
        <!-- <div v-if="status === 1 && needApproval" class="result-suggestion">导入审批中，请等待审批通过</div> -->

        <!-- 导入失败信息 -->
        <div v-if="status === 3 || status === 4" class="import-warning">{{ failedMsg }}</div>

        <!-- 导入结果（成功/失败）展示 （图片类型不展示）; 失败态下部分情况展示 -->
        <template v-if="!isImage && (status === 2 || (status === 3 && showErrDetail))">
          <div :style="`display:grid; grid-template-columns: ${resultSuccessPercent}fr ${resultFailedPercent}fr`" class="progress-bar">
            <div style="background: #96FFC6; border-radius: 10px 0px 0px 10px;"/>
            <div style="background: #FF6973; border-radius: 0px 10px 10px 0px;"/>
          </div>
          <div class="result-analysis flex justify-between">
            <span class="text-span">{{ resultSuccessNum }}条成功</span>
            <span class="text-span">{{ resultFailedNum }}条失败</span>
          </div>
        </template>

        <!-- 导入结果辅助文字提示 -->
        <div v-if="showImportResult" class="result-suggestion">
          <span>{{ taskStatusText }}</span>
        </div>
      </div>

      <!-- 操作栏 -->
      <div v-if="!importInPage" class="dialog-footer">
        <el-button :disabled="!(status === 2 || status === 3 || status === 4)" type="primary" @click="handleClose">确 定</el-button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { fetchImportTaskProgress, fetchImportResultUrl } from '@/api/public_import'
import { ref, watch, computed, onBeforeUnmount } from 'vue'
import { Message } from '@qs/ui'
// 任务失败态下展示【条数+下载+辅助文字】的错误状态码
const SHOW_DETAIL_ERRCODE = [0, 2008, 2009, 2010, 3000, 4000]
// 用审批流的bizId，在
// const APPROVAL_BIZ_ID = ['ASSET_BATCH_OFF', 'ASSET_BATCH_BANNED', 'ASSET_BATCH_UNBANND']

const props = defineProps({
  // 导入任务ID
  taskId: {
    type: String,
  },
  // 导入步骤是否嵌入页面
  importInPage: {
    type: Boolean,
    default: false,
  },
  // 上传内容是否为图片 true 则不展示导入条数部分
  isImage: {
    type: Boolean,
    default: false,
  },
  // 上传资源标识(业务线提供)
  // bizId: {
  //   type: String,
  //   required: true,
  // },
  // 轮询时间
  pollingTime: {
    type: Number,
    default: 1000 * 2,
  },
})

const loading = ref(true)
// 0初始状态,未开始读进度(获取taskId中)｜1导入中 读取进度中｜2导入成功｜3导入任务完成但失败｜4导入任务错误导致失败
const status = ref(0)
// 状态对应的展示文本
const statusText = ['导入中', '导入中', '导入完成', '导入失败', '导入失败']
// 当前导入步骤
const importStep = ref(0)
// 导入总步骤数（服务SDK给出）
const importStepTotal = 5
// 数据总条数
const totalNum = ref(0)
// 导入成功数量
const resultSuccessNum = ref(0)
// 导入失败数量
const resultFailedNum = ref(0)
// 导入结果下载地址
const resultDownload = ref('')
// 导入失败提示信息
const failedMsg = ref('')
// 导入任务响应数据，为兼容部分特殊场景回传需要
const response = ref({})
// 服务接口返回的任务状态 0进行中 1成功 2失败 3部分成功 10等待 11暂停
const taskStatus = ref(-1)
// 任务结果服务提示
const taskStatusText = '导入已完成，若导入失败请点击下载按钮完成明细查看。'
// 任务失败态错误码
const failCode = ref(null)
// 轮询 timer
let timer = null
// 导入任务审批等待中
// const needApproval = ref(false)

// 导入进度
const percentage = computed(() => {
  return (importStep.value / importStepTotal) * 100
})
// 成功比例
const resultSuccessPercent = computed(() => {
  return resultSuccessNum.value / totalNum.value * 100
})
// 失败比例
const resultFailedPercent = computed(() => {
  return resultFailedNum.value / totalNum.value * 100
})
// 任务失败态下是否展示【条数+下载+辅助文字】
const showErrDetail = computed(() => {
  return SHOW_DETAIL_ERRCODE.includes(failCode.value)
})

const showImportResult = computed(() => {
  return (status.value === 2 || status.value === 3 || status.value === 4) && !failedMsg.value
})

watch(() => props.taskId, (val) => {
  failedMsg.value = ''
  if (val) {
    pollingProgress()
    loading.value = false
    status.value = 1
  } else {
    status.value = 0
    taskStatus.value = -1
    response.value = {}
  }
}, { immediate: true })

const emits = defineEmits(['onImportFinish', 'onImportBtnClick', 'onClose'])
/** 轮询导入进度 */
function pollingProgress() {
  clearTimer()
  timer = setTimeout(() => {
    /** 服务SDK接口 */
    fetchImportTaskProgress({
      taskId: props.taskId,
    }).then((res) => {
      if (res && res.code === 1) {
        /**
         * status: 0初始状态,未开始读进度(获取taskId中)｜1导入中 读取进度中｜2导入成功｜3导入任务完成但失败｜4导入任务错误导致失败
         * taskStatus: 0进行中 1成功 2失败 3部分成功 10等待 11暂停
         */
        const data = res.data || {}
        if (data.taskStatus === 1 || data.taskStatus === 3) { // 前端展示对 [1和3] 都按照导入成功文案处理
          status.value = 2
          totalNum.value = data.parsedCount || (data.successCount + data.failureCount) || 0
          resultSuccessNum.value = data.successCount || 0
          resultFailedNum.value = data.failureCount || 0
          resultDownload.value = data.situationPath || '' // 下载地址，不一定有
          emits('onImportFinish', props.taskId, data.taskStatus, data.failMsg || '', data) // 导入完成事件
          clearTimer()
        } else if (data.taskStatus === 2) {
          status.value = 3
          totalNum.value = data.parsedCount || (data.successCount + data.failureCount) || 0
          resultSuccessNum.value = data.successCount || 0
          resultFailedNum.value = data.failureCount || 0
          failedMsg.value = data.failMsg || ''
          resultDownload.value = data.situationPath || '' // 下载地址，不一定有
          failCode.value = data.failCode
          emits('onImportFinish', props.taskId, data.taskStatus, data.failMsg || '', data) // 导入完成事件
          clearTimer()
        } else {
          // 任务等待审批暂停中 展示辅助提示文案，仅部分模块
          // if (APPROVAL_BIZ_ID.includes(props.bizId) && data.taskStatus === 11) {
          //   needApproval.value = true
          // } else {
          //   needApproval.value = false
          // }
          data.updatePhase && (importStep.value = data.updatePhase)
          pollingProgress()
        }
      } else {
        // 接口响应其他响应码，失败
        status.value = 4
        failedMsg.value = res.data.msg || ''
        emits('onImportFinish', props.taskId, 2, res.msg || '', res.data || {}) // 导入完成事件
        clearTimer()
      }
      /** 特殊场景需要 */
      response.value = res.data || {}
      taskStatus.value = res.data.taskStatus
    }).catch((err) => {
      status.value = 4
      failedMsg.value = err.msg
      response.value = {}
      taskStatus.value = 2
      emits('onImportFinish', props.taskId, 2, err.msg || '', { failMsg: err.msg }) // 导入完成事件，状态写死2失败，失败也从这里透出
      clearTimer()
    })
  }, props.pollingTime)
}

function clearTimer() {
  clearTimeout(timer)
  timer = null
}

/** 下载结果明细 */
function fetchResultUrl() {
  if (!props.taskId) {
    return Message({
      type: 'error',
      message: '缺失任务ID，无法下载',
    })
  }
  fetchImportResultUrl({
    taskId: props.taskId,
  }).then((res) => {
    if (res && res.code === 1) {
      const ele = document.createElement('a')
      ele.setAttribute('href', res.data)
      ele.style.display = 'none'
      document.body.appendChild(ele)
      ele.click()
      document.body.removeChild(ele)
    } else {
      Message({
        type: 'error',
        message: '服务器异常，请稍后重试',
      })
    }
  })
}

/** 关闭导入进度弹框 */
function handleClose() {
  emits('onImportBtnClick', props.taskId, taskStatus.value, response.value.failMsg || '', response.value)
  emits('onClose', false)
  clearTimer()
}

onBeforeUnmount(() => {
  clearTimer()
})
</script>

<style lang="scss" scoped>
.flex {
  display: flex;
  &.flex-col {
    flex-direction: column;
  }
  &.justify-between {
    justify-content: space-between;
  }
  &.justify-center {
    justify-content: center;
  }
  &.justify-around {
    justify-content: space-around;
  }
  &.justify-end {
    justify-content: flex-end;
  }
  &.align-center {
    align-items: center;
  }
}

.border-box {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 12px 24px 12px 12px;
  margin-top: 12px;
  max-width: 500px;
}

.twoCols {
  display: grid;
  grid-template-columns: 90px auto;
  align-items: baseline;
  padding: 0px 16px;
  height: 230px;
}

.import-content {
  .import-status-msg {
    margin-bottom: 5px;
  }
  .text-span {
    color: #303133;
    &.icon-left i {
      margin-right: 5px;
    }
    &.icon-right i {
      margin-left: 5px;
    }
  }
  .progress-bar {
    width: 100%;
    height: 5px;
    position: relative;
    grid-template-rows: 1fr;
    margin-bottom: 5px;
    margin-top: 12px;
  }
  .import-warning {
    color: #FF5964;
  }
  .result-suggestion {
    color: #606266;
    font-size: 12px;
    margin-top: 12px;
  }
  .dialog-footer {
    margin-top: 32px;
  }
}
</style>
